/**
 * User React Query Hooks
 *
 * With Better Auth, session cookies are sent automatically.
 * No token passing needed - just call the API functions directly.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as userApi from "../api/users";
import { authClient } from "../auth-client";
import type { UserPreferences, UserProfileResponse } from "../api/types";
import { ANALYTICS_CONTEXT_QUERY_KEY } from "./use-analytics-context";

export const USER_QUERY_KEY = "user";
export const SESSIONS_QUERY_KEY = "sessions";
export const ACCOUNTS_QUERY_KEY = "accounts";

/**
 * Better Auth client methods resolve to `{ data, error }` rather than
 * throwing (see e.g. apps/web/app/forgot-password/page.tsx checking
 * `result.error`). TanStack Query's `isError`/`onError` and plain
 * try/catch callers both depend on a rejected promise, so every mutation
 * and query below unwraps that shape and throws on `error`.
 */
function unwrapAuthResult<T>(result: {
  data: T | null;
  error: { message?: string } | null;
}): T {
  if (result.error) {
    throw new Error(
      result.error.message || "Something went wrong. Please try again.",
    );
  }
  return result.data as T;
}

/**
 * Fetch user profile (identity, preferences, subscription tier, activity
 * counters, timestamps) from the application API.
 */
export function useUserProfile() {
  return useQuery({
    queryKey: [USER_QUERY_KEY, "profile"],
    queryFn: () => userApi.getUserProfile(),
  });
}

/**
 * Update Batch & Bowl preferences (dietary restrictions, household size,
 * skill level, etc.) via PUT /users/preferences.
 *
 * Identity fields (name, image, email) are NOT handled here — they are
 * owned by Better Auth. Use `useUpdateIdentity()` for those.
 */
/**
 * Download the full user data export (see `exportUserData` /
 * GET /users/export). Returns raw JSON text; the caller is responsible for
 * turning it into a file download (mirrors `useExportGroceryList`).
 */
export function useExportUserData() {
  return useMutation({
    mutationFn: () => userApi.exportUserData(),
  });
}

export function useUpdatePreferences() {
  const queryClient = useQueryClient();
  const profileKey = [USER_QUERY_KEY, "profile"];

  return useMutation({
    mutationFn: (preferences: UserPreferences) =>
      userApi.updateUserPreferences(preferences),
    onSuccess: (profile: UserProfileResponse) => {
      // PUT returns the authoritative whole profile. Publish it before the
      // background GET so every preferences surface keeps a durable update
      // even if that reconciliation request is temporarily unavailable.
      queryClient.setQueryData<UserProfileResponse>(profileKey, profile);
      queryClient.invalidateQueries({ queryKey: profileKey });
      queryClient.invalidateQueries({
        queryKey: [ANALYTICS_CONTEXT_QUERY_KEY],
      });
    },
  });
}

/**
 * Persist the user's own aisle order for the grocery list (in-store mode,
 * E10) via PATCH /users/aisle-order. Optimistically patches the cached
 * profile's `aisleOrder` so the reordered sections apply the moment the
 * user saves (this is used mid-shop, where a round-trip wait -- or a
 * flash back to the old order -- reads as a lost edit), rolling back only
 * if the server rejects the write.
 */
export function useUpdateAisleOrder() {
  const queryClient = useQueryClient();
  const profileKey = [USER_QUERY_KEY, "profile"];

  return useMutation({
    mutationFn: (aisleOrder: string[]) => userApi.updateAisleOrder(aisleOrder),
    onMutate: async (aisleOrder) => {
      await queryClient.cancelQueries({ queryKey: profileKey });
      const previousProfile = queryClient.getQueryData(profileKey);
      queryClient.setQueryData(
        profileKey,
        (old: Record<string, unknown> | undefined) =>
          old ? { ...old, aisleOrder } : old,
      );
      return { previousProfile };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousProfile !== undefined) {
        queryClient.setQueryData(profileKey, context.previousProfile);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileKey });
    },
  });
}

/**
 * Stamp onboarding completion (Finish or Skip in the preferences wizard).
 * Invalidates the profile query so App.tsx's routing input refreshes.
 */
export function useCompleteOnboarding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => userApi.completeOnboarding(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USER_QUERY_KEY, "profile"] });
    },
  });
}

/**
 * Update Better Auth identity fields (name, image). This calls
 * `authClient.updateUser` directly rather than the application API —
 * PUT /users/profile does not exist; identity mutations are Better Auth's
 * responsibility, not this API's.
 *
 * Better Auth's client automatically refreshes its own session store after
 * `updateUser` succeeds (its `/update-user` atom listener triggers a
 * `$sessionSignal` refetch), so any component using `authClient.useSession()`
 * picks up the new name/image without extra plumbing here. We additionally
 * invalidate `["user", "profile"]` so `useUserProfile()` (backed by this
 * app's API, not Better Auth's session) stays in sync too.
 */
export function useUpdateIdentity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (identity: { name?: string; image?: string | null }) =>
      authClient.updateUser(identity),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USER_QUERY_KEY, "profile"] });
    },
  });
}

/**
 * Request an email change. Better Auth sends a confirmation link to the
 * user's CURRENT email address (see `sendChangeEmailConfirmation` in
 * apps/api/src/auth.ts) — the address on the account only changes once
 * that link is followed, so this call itself never invalidates the
 * session or profile cache.
 */
export function useChangeEmail() {
  return useMutation({
    mutationFn: (input: { newEmail: string; callbackURL?: string }) =>
      authClient.changeEmail(input).then(unwrapAuthResult),
  });
}

/**
 * Change the signed-in user's password. Callers are responsible for
 * passing `revokeOtherSessions: true` (Global constraint: password
 * changes must revoke other sessions) — kept explicit here rather than
 * hardcoded so the security-sensitive behavior is visible and testable
 * at the call site (see AccountSecurity.tsx / AccountSecurity.test.tsx).
 *
 * Stale-session-store fix: unlike `/update-user`, `/revoke-session`,
 * `/sign-out`, etc., `/change-password` is NOT in Better Auth's client
 * `$sessionSignal` atom matcher list (see the `atomListeners` matcher in
 * node_modules/better-auth/dist/client/config.mjs, ~line 71). When
 * `revokeOtherSessions: true` is passed, the server rotates the CURRENT
 * session's token (better-auth's `/change-password` handler:
 * `deleteSessions` -> `createSession` -> `setSessionCookie` — see
 * node_modules/better-auth/dist/api/routes/update-user.mjs) and refreshes
 * both the session cookie and its cookie cache
 * (node_modules/better-auth/dist/cookies/index.mjs, `setSessionCookie`
 * calls `setCookieCache`). But because the matcher list skips this path,
 * nothing tells the client's `session` nanostore (the one
 * `authClient.useSession()` reads via `useSyncExternalStore`) to refetch,
 * so it keeps serving the pre-rotation token.
 *
 * `authClient.getSession()` does NOT fix this: it is a plain proxied
 * fetch to `/get-session` (see client/proxy.mjs) that updates only its
 * own call-site data, not the shared `session` atom — and `/get-session`
 * itself isn't in the matcher list either, so it doesn't flip
 * `$sessionSignal` as a side effect.
 *
 * What actually writes the shared store: flipping the `$sessionSignal`
 * atom is what the built-in matched-path listeners do
 * (`signal.set(!val)` in proxy.mjs), and that flip is what causes the
 * `session` atom's internal subscriber (registered in
 * client/session-atom.mjs / query.mjs) to refetch `/get-session` and
 * write the result into the atom `useSession()` subscribes to.
 * `authClient.$store.notify("$sessionSignal")` is the same operation,
 * exposed as public, typed API (see client/vanilla.d.mts: `$store: {
 * notify: (signal?: ... "$sessionSignal") => void; ... }`, and it's
 * exercised the same way internally by e.g. the organization plugin). So
 * we call it explicitly here to force `useSession()` (and anything
 * comparing against its token, like ActiveSessions' "Current session"
 * badge / revoke-eligibility check) to pick up the rotated token.
 */
export function useChangePassword() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      currentPassword: string;
      newPassword: string;
      revokeOtherSessions?: boolean;
    }) => authClient.changePassword(input).then(unwrapAuthResult),
    onSuccess: () => {
      authClient.$store.notify("$sessionSignal");
      queryClient.invalidateQueries({
        queryKey: [USER_QUERY_KEY, SESSIONS_QUERY_KEY],
      });
    },
  });
}

/**
 * Reuses the existing forgot-password flow to let an OAuth-only user add
 * a credential (password) to their account: Better Auth's
 * `/reset-password` handler creates a "credential" account for the user
 * if one doesn't already exist (see better-auth's
 * dist/api/routes/password.mjs), so no custom password-setting endpoint
 * is needed.
 */
export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: (input: { email: string; redirectTo?: string }) =>
      authClient.requestPasswordReset(input).then(unwrapAuthResult),
  });
}

/**
 * Request account deletion via Better Auth. `apps/api/src/auth.ts`
 * configures `user.deleteUser.sendDeleteAccountVerification`, and Better
 * Auth's `/delete-user` handler (node_modules/better-auth/dist/api/routes/
 * update-user.mjs) checks that callback BEFORE its immediate-delete branch
 * — so whenever it's configured (as it is here), calling this always
 * queues a confirmation email and returns `{ message: "Verification email
 * sent" }` rather than deleting synchronously, REGARDLESS of whether a
 * `password` was supplied. Passing `password` for credential accounts only
 * makes the server verify it before sending that email (an extra proof of
 * intent) — it does not skip the email step. The account is only actually
 * deleted once the user clicks the emailed link, which hits Better Auth's
 * `/delete-user/callback` route directly (a plain GET the browser follows,
 * not something this app's client code drives).
 *
 * This is why `DeleteAccountDialog` shows the same "check your email to
 * finish deleting your account" messaging for credential AND OAuth-only
 * users, not just OAuth-only ones.
 */
export function useDeleteAccount() {
  return useMutation({
    mutationFn: (input: { password?: string; callbackURL?: string }) =>
      authClient.deleteUser(input).then(unwrapAuthResult),
  });
}

/** List the signed-in user's active sessions (across devices/browsers). */
export function useSessions() {
  return useQuery({
    queryKey: [USER_QUERY_KEY, SESSIONS_QUERY_KEY],
    queryFn: () => authClient.listSessions().then(unwrapAuthResult),
  });
}

export function useRevokeSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (token: string) =>
      authClient.revokeSession({ token }).then(unwrapAuthResult),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [USER_QUERY_KEY, SESSIONS_QUERY_KEY],
      });
    },
  });
}

export function useRevokeOtherSessions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => authClient.revokeOtherSessions().then(unwrapAuthResult),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [USER_QUERY_KEY, SESSIONS_QUERY_KEY],
      });
    },
  });
}

/** List OAuth/credential accounts linked to the signed-in user. */
export function useAccounts() {
  return useQuery({
    queryKey: [USER_QUERY_KEY, ACCOUNTS_QUERY_KEY],
    queryFn: () => authClient.listAccounts().then(unwrapAuthResult),
  });
}

export function useLinkSocial() {
  return useMutation({
    mutationFn: (input: { provider: "google"; callbackURL?: string }) =>
      authClient.linkSocial(input).then(unwrapAuthResult),
  });
}

/**
 * Unlink a provider from the signed-in user's account. The server keeps
 * `allowUnlinkingAll` at its default (false; see apps/api/src/auth.ts,
 * which does not set `account.accountLinking`), so Better Auth itself
 * refuses to remove the last remaining sign-in method even if a client
 * bug lets this get called — the disabled-button UX in LinkedAccounts.tsx
 * is a courtesy, not the enforcement boundary.
 */
export function useUnlinkAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { providerId: string; accountId?: string }) =>
      authClient.unlinkAccount(input).then(unwrapAuthResult),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [USER_QUERY_KEY, ACCOUNTS_QUERY_KEY],
      });
    },
  });
}
